import { GuessResult } from '../types';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface ResultRowProps {
    guess: GuessResult;
    translateValue: (type: string, value: string) => string;
    index: number;
}

export default function ResultRow({ guess, translateValue, index }: ResultRowProps) {
    const { character, comparisons } = guess;

    const getStatusClass = (status: 'correct' | 'wrong' | 'partial') => {
        switch (status) {
            case 'correct': return 'status-correct';
            case 'partial': return 'status-partial';
            default: return 'status-wrong';
        }
    };

    const renderCell = (
        value: string,
        status: 'correct' | 'wrong' | 'partial',
        direction?: 'up' | 'down'
    ) => (
        <div className={`result-cell ${getStatusClass(status)}`} style={{ animationDelay: `${index * 0.05}s` }}>
            <span>{value}</span>
            {direction && (
                <span className="ml-1">
                    {direction === 'up' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
            )}
        </div>
    );

    return (
        <div className="grid-results result-row mb-1" style={{ animationDelay: `${index * 0.1}s` }}>
            {/* Character Name & Avatar */}
            <div className="character-cell rounded-l-lg">
                <img
                    src={character.image}
                    alt={character.name}
                    className="character-avatar"
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/placeholder.webp';
                    }}
                />
                <span className="font-medium text-sm truncate">{character.name}</span>
            </div>

            {/* Gender */}
            {renderCell(
                translateValue('gender', character.gender),
                comparisons.gender
            )}

            {/* Arc */}
            {renderCell(
                translateValue('arc', character.arc),
                comparisons.arc,
                comparisons.arc !== 'correct' ? comparisons.arcDirection : undefined
            )}

            {/* Affiliation */}
            {renderCell(
                translateValue('affiliation', character.affiliation),
                comparisons.affiliation
            )}

            {/* Species */}
            {renderCell(
                translateValue('species', character.species),
                comparisons.species
            )}

            {/* Height */}
            {renderCell(
                translateValue('height', character.height),
                comparisons.height,
                comparisons.height !== 'correct' ? comparisons.heightDirection : undefined
            )}

            {/* Status */}
            <div className={`result-cell rounded-r-lg ${getStatusClass(comparisons.status)}`}>
                {translateValue('status', character.status)}
            </div>
        </div>
    );
}
